/* eslint-disable react/no-this-in-sfc */
import React, { useMemo, useState, useEffect } from 'react';
import { useAsync } from 'react-async';
import { add, startOfMonth } from 'date-fns';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Loader from 'components/Loader';
import { useProduct } from 'contexts/ProductContext';
import { getWeightedAverageAnnualizedLossRate } from 'api/performanceMetric';
import abbreviateNumber from 'utils/abbreviateNumber';
import theme from 'theme';
import {
  getMonthlySeriesDefault,
  populateMonthlySeries,
  selectMonthlyDateLabel,
  MONTHS_IN_YEAR, selectAttribute,
} from 'utils/monthlySeries';
import useLegendStyles from 'theme/legendStyle';

const { highlight } = theme.palette;
const { tooltip, legend, chart } = theme.chartStyle;

const EWAALR = 'weightedAverageAnnualizedLossRate';

const MonthlyWeightedAverageLossesAndNetReturns = () => {
  const legendClasses = useLegendStyles({ color: highlight.main });

  const [series, setSeries] = useState([]);
  const { currentProductKey } = useProduct();

  // the underlying Look only ever returns a trailing 12 months of data
  const startingDate = useMemo(
    () => startOfMonth(add(Date.now(), { months: -MONTHS_IN_YEAR })),
    [],
  );
  const seriesDefault = useMemo(
    () => getMonthlySeriesDefault(startingDate, MONTHS_IN_YEAR, { [EWAALR]: 0 }),
    [startingDate],
  );

  // populate series from api
  const { data, isLoading, isFulfilled } = useAsync(getWeightedAverageAnnualizedLossRate, {
    productKey: currentProductKey,
    watch: currentProductKey,
  });

  const monthlyData = data?.data?.data || [];
  useEffect(() => {
    setSeries(
      populateMonthlySeries(seriesDefault, monthlyData),
    );
  }, [isFulfilled]);

  const ewaalrData = useMemo(() => series.map(selectAttribute(EWAALR) ?? 0), [series]);
  const monthlyLabels = useMemo(
    () => series.map(selectMonthlyDateLabel(new Date(), false)),
    [series],
  );

  const highchartSeries = [
    {
      data: ewaalrData,
      name: 'Expected weighted average annualized loss rates',
      pointPlacement: 'on',
      lineWidth: 1,
      lineColor: highlight.main,
      style: { marginRight: 24 },
      color: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, Highcharts.color(highlight.main).setOpacity(0.4).get('rgba')],
          [1, Highcharts.color(highlight.main).setOpacity(0.0).get('rgba')],
        ],
      },
      marker: {
        enabled: false,
        fillColor: highlight.main,
        lineColor: highlight.main,
      },
    },
  ];

  const options = {
    series: highchartSeries,
    legend: {
      ...legend,
      className: legendClasses.chartLegend,
      itemStyle: {
        ...legend.itemStyle,
        color: highlight.main,
      },
    },
    offset: -15,
    title: {
      text: '',
    },
    plotOptions: {
      area: {
        dataLabels: {
          enabled: true,
          format: '{y:.2f}%',
          color: highlight.main,
          padding: 15,
          alignment: 'right',
          style: {
            fontWeight: 'normal',
          },
        },
      },
    },
    chart: {
      style: {
        ...chart.style,
      },
      marginRight: 72,
      type: 'area',
    },
    xAxis: [{
      offset: 3,
      tickAmount: 12,
      categories: monthlyLabels,
      crosshair: true,
      labels: {
        step: 1,
        y: 32,
        style: { fontSize: 14 },
        styledMode: true,
        format: '{value}',
        formatter() {
          // eslint-disable-next-line react/no-this-in-sfc
          const label = this.axis.defaultLabelFormatter.call(this);
          return [label.split(/\s/)].map(x => `${x[0].slice(0, 3)} ${x[1]}`)[0].replace(/\s/, '<br>');
        },
      },
    }],
    yAxis: {
      offset: 2,
      gridLineWidth: 0,
      labels: {
        format: '{value}',
        formatter() {
          const label = this.axis.defaultLabelFormatter.call(this);
          return `${abbreviateNumber(label)}.0%`;
        },
        style: {
          color: '#757575',
          fontSize: 12,
          backgroundColor: 'white',
        },
        step: 1,
      },
      title: {
        text: '',
      },
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      ...tooltip,
      headerFormat: '',
      pointFormatter() {
        return `
          <strong>${monthlyLabels[this.x].replace('<br/>', ' ')}</strong><br>
          <p>${this.y} Expected Weighted Average Annualized Loss Rate</p><br />
        `;
      },
    },
  };

  return (
    <>
      {
        isLoading ?
          <Loader data-testid="loader" /> :
          (
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
            />
          )
      }
    </>
  );
};

export default MonthlyWeightedAverageLossesAndNetReturns;
